// SceneCameraRig — camera animation driven by window.__planetProgress
// in narrative mode, or by user drag / auto-orbit in exploration mode.
//
// Narrative mode
// ==============
// Reads the smoothed scroll value, picks the current scene's camera
// anchor, and moves the R3F default camera toward it with a "framing"
// ease (dwells at each scene, transitions quickly across the boundary
// using smootherstep) + a subtle breathing oscillation.
//
// Exploration mode
// ================
// Scroll-driven lerp is disabled. Instead:
//   - The camera orbits the planet center as a (azimuth, elevation,
//     distance) spherical tuple.
//   - Mouse drag updates azimuth + elevation directly.
//   - After 2 seconds of no drag input, auto-orbit kicks in
//     (azimuth += 0.02 rad/s).
//   - The mouse wheel zooms the orbit distance (clamped 3..15).
//   - lookAt is always [0, 0, 0] (planet center).
//
// Mode transition
// ===============
// When switching from exploration → narrative, the rig holds the
// current camera position for 800ms (so the user can read the
// transition as a deliberate pause) then eases back to the
// scroll-driven target over 1.2s using smootherstep.
//
// Both modes share the same R3F camera object; no scene tree changes
// are required.

import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

import { SCENES } from './planetSections';
import { getActiveScene } from './sceneState';
import type { PlanetMode } from './useModeController';

/** Smootherstep (Ken Perlin) — flatter at both ends than smoothstep. */
const SMOOTHERSTEP = (x: number) => {
  const t = x < 0 ? 0 : x > 1 ? 1 : x;
  return t * t * t * (t * (t * 6 - 15) + 10);
};

/** Tour scheduling — must match useTourController.ts constants. */
const TOUR_DWELL_MS = 8000;
const TOUR_TRANSITION_MS = 2000;
const TOUR_CYCLE_MS = TOUR_DWELL_MS + TOUR_TRANSITION_MS;

/** Fraction of a scene (measured from its end) over which the camera
 *  transitions to the next scene's anchor. Smaller = longer dwell +
 *  snappier transition = more "framed". */
const TRANSITION_BAND = 0.45;

const BREATHE_PERIOD = 8; // seconds
const BREATHE_AMPLITUDE = 0.05;
const TWO_PI = Math.PI * 2;

// Nonlinear flight — chapter-to-chapter transitions ride a quadratic
// bezier whose control point swings outward from the planet and lifts
// (or dips, alternating per chapter), so the camera swoops between
// scenes instead of sliding a straight line. The gaze also sweeps
// toward the planet center at mid-flight.
const ARC_SWING = 0.42; // outward push, fraction of anchor distance
const ARC_LIFT = 0.34; // vertical arc, fraction of anchor distance
const GAZE_SWEEP = 0.3; // how far the lookAt pulls to the planet center
const ORIGIN = new THREE.Vector3(0, 0, 0);
const _arcP0 = new THREE.Vector3();
const _arcP2 = new THREE.Vector3();
const _arcMid = new THREE.Vector3();
const _arcDir = new THREE.Vector3();

// Scroll-velocity flight feel (narrative mode only).
const BASE_FOV = 42;
const FOV_STRETCH_MAX = 6; // extra degrees at full scroll speed
const ROLL_MAX = 0.07; // radians of camera roll at full scroll speed

/** Ease the FOV back to its resting value (used by non-narrative modes). */
function relaxFov(camera: THREE.Camera, delta: number) {
  const pc = camera as THREE.PerspectiveCamera;
  if (!pc.isPerspectiveCamera) return;
  if (Math.abs(pc.fov - BASE_FOV) < 0.02) return;
  pc.fov += (BASE_FOV - pc.fov) * (1 - Math.pow(0.9, delta * 60));
  pc.updateProjectionMatrix();
}

// Exploration tunables.
const EXPLORATION_IDLE_MS = 2000;
const EXPLORATION_AUTO_ORBIT_RAD_PER_S = 0.02;
const EXPLORATION_DRAG_SENSITIVITY = 0.0055;
const EXPLORATION_ELEVATION_MIN = -Math.PI * 0.45; // can't tilt upside down
const EXPLORATION_ELEVATION_MAX = Math.PI * 0.45;
const EXPLORATION_ZOOM_MIN = 3;
const EXPLORATION_ZOOM_MAX = 15;

// Mode-transition tunables (exploration → narrative).
const TRANSITION_HOLD_MS = 800;
const TRANSITION_EASE_MS = 1200;

// Augment window so other modules can read mode-specific bits.
declare global {
  interface Window {
    /** Set externally to dismiss the info card + resume auto-orbit. */
    __planetDismissInfoCard?: () => void;
    /** Set externally to pause/resume auto-orbit (SPACE key). */
    __planetAutoOrbitPaused?: boolean;
    /** Camera distance override (set by wheel). */
    __planetOrbitDistance?: number;
    /** Spherical orbit state (read by HUD for telemetry / tests). */
    __planetOrbit?: { azimuth: number; elevation: number; distance: number };
  }
}

export interface SceneCameraRigProps {
  /** While true, the camera freezes at the current scene anchor. */
  isDraggingRef?: React.MutableRefObject<boolean>;
  /** Drag delta in radians (kept for symmetry; doesn't affect camera). */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  dragDeltaRef?: React.MutableRefObject<number>;
}

/**
 * Compute the "framed" camera target for scroll progress `p`.
 * Dwells at the current scene anchor, then transitions across the
 * TRANSITION_BAND near the boundary using smootherstep.
 */
function framedCameraTarget(
  p: number,
  outPos: THREE.Vector3,
  outLook: THREE.Vector3,
) {
  const clamped = p < 0 ? 0 : p > 1 ? 1 : p;
  // Locate the active scene + how far through it we are (blendIn 0..1).
  let idx = 0;
  let blendIn = 0;
  for (let i = 0; i < SCENES.length; i++) {
    const s = SCENES[i];
    if (clamped >= s.start && clamped <= s.end) {
      idx = i;
      const span = s.end - s.start;
      blendIn = span > 0 ? (clamped - s.start) / span : 0;
      break;
    }
    if (i === SCENES.length - 1) {
      idx = i;
      blendIn = 1;
    }
  }

  const cur = SCENES[idx];
  const next = SCENES[Math.min(idx + 1, SCENES.length - 1)];

  // Dwell for the first (1 - TRANSITION_BAND) of the scene, then ramp.
  let t = 0;
  const dwellEnd = 1 - TRANSITION_BAND;
  if (blendIn > dwellEnd) {
    t = SMOOTHERSTEP((blendIn - dwellEnd) / TRANSITION_BAND);
  }

  _arcP0.set(cur.cameraPos[0], cur.cameraPos[1], cur.cameraPos[2]);
  _arcP2.set(next.cameraPos[0], next.cameraPos[1], next.cameraPos[2]);

  if (t <= 0 || idx >= SCENES.length - 1) {
    outPos.copy(_arcP0);
  } else {
    // Quadratic bezier: control point = midpoint pushed radially away
    // from the planet + an alternating vertical lift, so consecutive
    // flights arc in opposite directions (over, then under).
    const span = _arcP0.distanceTo(_arcP2);
    _arcMid.addVectors(_arcP0, _arcP2).multiplyScalar(0.5);
    _arcDir.copy(_arcMid);
    if (_arcDir.lengthSq() < 1e-6) _arcDir.set(0, 0, 1);
    _arcDir.normalize();
    _arcMid.addScaledVector(_arcDir, span * ARC_SWING);
    _arcMid.y += (idx % 2 === 0 ? 1 : -1) * span * ARC_LIFT;

    const u = 1 - t;
    outPos.set(
      u * u * _arcP0.x + 2 * u * t * _arcMid.x + t * t * _arcP2.x,
      u * u * _arcP0.y + 2 * u * t * _arcMid.y + t * t * _arcP2.y,
      u * u * _arcP0.z + 2 * u * t * _arcMid.z + t * t * _arcP2.z,
    );
  }

  outLook.set(
    cur.cameraLookAt[0] + (next.cameraLookAt[0] - cur.cameraLookAt[0]) * t,
    cur.cameraLookAt[1] + (next.cameraLookAt[1] - cur.cameraLookAt[1]) * t,
    cur.cameraLookAt[2] + (next.cameraLookAt[2] - cur.cameraLookAt[2]) * t,
  );
  // Mid-flight the gaze sweeps across the planet's face before settling
  // on the next chapter's focal point.
  const sweep = Math.sin(t * Math.PI) * GAZE_SWEEP;
  if (sweep > 0) outLook.lerp(ORIGIN, sweep);

  // Every chapter has a small independent approach. The first and last
  // frames stay composed like an establishing shot, while the middle of a
  // chapter leans into its landmark or city slice. This creates an orbit →
  // surface → orbit rhythm without adding a second camera system.
  const dolly = cur.cameraDolly + (next.cameraDolly - cur.cameraDolly) * t;
  const chapterPush = Math.sin(blendIn * Math.PI) * dolly;
  outPos.lerp(outLook, chapterPush);
}

export function SceneCameraRig({ isDraggingRef }: SceneCameraRigProps) {
  const camera = useThree((s) => s.camera) as THREE.Camera;
  const targetPos = useRef(new THREE.Vector3());
  const targetLook = useRef(new THREE.Vector3());
  const tmpPos = useRef(new THREE.Vector3());
  const tmpLook = useRef(new THREE.Vector3());
  const breathePos = useRef(new THREE.Vector3());
  // Smoothed camera roll driven by scroll velocity (narrative mode).
  const rollRef = useRef(0);

  // Narrative-mode anchor for hold / release.
  const holdPos = useRef(new THREE.Vector3(0, 0.5, 8));
  const holdLook = useRef(new THREE.Vector3(0, 0, 0));
  const releaseT0 = useRef<number | null>(null);
  const releaseFromPos = useRef(new THREE.Vector3());
  const releaseFromLook = useRef(new THREE.Vector3());
  const lastIsDragging = useRef(false);

  // Mode bookkeeping.
  const modeRef = useRef<PlanetMode>(
    typeof window !== 'undefined' && window.__planetMode
      ? window.__planetMode
      : 'narrative',
  );
  const transitionT0 = useRef<number | null>(null);
  const transitionPhase = useRef<'idle' | 'hold' | 'ease'>('idle');
  const transitionFromPos = useRef(new THREE.Vector3());
  const transitionFromLook = useRef(new THREE.Vector3());
  const transitionTargetPos = useRef(new THREE.Vector3());
  const transitionTargetLook = useRef(new THREE.Vector3());

  // Exploration orbit state.
  const azimuthRef = useRef(0);
  const elevationRef = useRef(0.2);
  const distanceRef = useRef(8);
  const orbitPos = useRef(new THREE.Vector3());

  // Click-to-fly state.
  const flyT0 = useRef<number | null>(null);
  const flyFromPos = useRef(new THREE.Vector3());
  const flyFromLook = useRef(new THREE.Vector3());
  const flyTargetPos = useRef(new THREE.Vector3());
  const flyTargetLook = useRef(new THREE.Vector3());

  // Initialize camera to scene 0 anchor.
  useEffect(() => {
    const s0 = getActiveScene(0);
    camera.position.set(s0.cameraPos[0], s0.cameraPos[1], s0.cameraPos[2]);
    camera.lookAt(s0.cameraLookAt[0], s0.cameraLookAt[1], s0.cameraLookAt[2]);
    holdPos.current.set(s0.cameraPos[0], s0.cameraPos[1], s0.cameraPos[2]);
    holdLook.current.set(
      s0.cameraLookAt[0],
      s0.cameraLookAt[1],
      s0.cameraLookAt[2],
    );
    // Seed the exploration distance from the active scene's distance so
    // the zoom slider starts in a sensible place.
    const dist = Math.hypot(s0.cameraPos[0], s0.cameraPos[1], s0.cameraPos[2]);
    distanceRef.current = Math.min(
      EXPLORATION_ZOOM_MAX,
      Math.max(EXPLORATION_ZOOM_MIN, dist),
    );
  }, [camera]);

  // Time of last drag/wheel input — drives the auto-orbit idle gate.
  const lastInputT = useRef(0);

  // -----------------------------------------------------------------
  // Subscribe to mode changes; install pointer/wheel listeners for
  // exploration mode.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let lastMode = window.__planetMode ?? 'narrative';
    const id = window.setInterval(() => {
      const cur = window.__planetMode ?? 'narrative';
      if (cur !== lastMode) {
        const from = lastMode;
        const to = cur;
        lastMode = cur;
        modeRef.current = to;
        onModeChange(from, to);
      }
    }, 100);

    function onModeChange(from: PlanetMode, to: PlanetMode) {
      // Clear any in-flight click-to-fly.
      flyT0.current = null;
      if (to === 'exploration') {
        // Snapshot the current orbit pose so the toggle is instant.
        const p = camera.position;
        const dist = Math.max(0.001, Math.hypot(p.x, p.y, p.z));
        distanceRef.current = Math.min(
          EXPLORATION_ZOOM_MAX,
          Math.max(EXPLORATION_ZOOM_MIN, dist),
        );
        azimuthRef.current = Math.atan2(p.x, p.z);
        elevationRef.current = Math.asin(p.y / dist);
        // Publish telemetry.
        window.__planetOrbit = {
          azimuth: azimuthRef.current,
          elevation: elevationRef.current,
          distance: distanceRef.current,
        };
        // Suppress the swipe gestures while exploring.
        window.__swipeDisabled = true;
        // Reset idle timer so auto-orbit kicks in after the standard 2s.
        lastInputT.current = performance.now();
      } else {
        // narrative — start the hold + ease-back transition.
        window.__swipeDisabled = false;
        transitionPhase.current = 'hold';
        transitionT0.current = performance.now();
        transitionFromPos.current.copy(camera.position);
        transitionFromLook.current.set(0, 0, 0);
        // Compute the scroll-driven target for the current scene.
        const p = window.__planetProgress ?? 0;
        framedCameraTarget(p, transitionTargetPos.current, transitionTargetLook.current);
      }
    }

    // Pointer listeners (drag-to-orbit in exploration).
    let lastX = 0;
    let lastY = 0;
    const onPointerDown = (e: PointerEvent) => {
      if (modeRef.current !== 'exploration') return;
      // Don't capture clicks that originate from HUD UI.
      const target = e.target as HTMLElement | null;
      if (target && target.closest('.hud-root')) return;
      if (e.button !== undefined && e.button !== 0) return;
      __planetRigActivePointerId = e.pointerId;
      lastX = e.clientX;
      lastY = e.clientY;
      lastInputT.current = performance.now();
      try {
        (e.target as Element | null)?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
    };
    const onPointerMove = (e: PointerEvent) => {
      if (modeRef.current !== 'exploration') return;
      if (__planetRigActivePointerId !== e.pointerId) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      azimuthRef.current -= dx * EXPLORATION_DRAG_SENSITIVITY;
      elevationRef.current = Math.max(
        EXPLORATION_ELEVATION_MIN,
        Math.min(
          EXPLORATION_ELEVATION_MAX,
          elevationRef.current + dy * EXPLORATION_DRAG_SENSITIVITY,
        ),
      );
      lastInputT.current = performance.now();
      // Cancel any in-flight click-to-fly.
      flyT0.current = null;
    };
    const onPointerUp = (e: PointerEvent) => {
      if (__planetRigActivePointerId !== e.pointerId) return;
      __planetRigActivePointerId = null;
      lastInputT.current = performance.now();
    };
    const onPointerCancel = (e: PointerEvent) => {
      if (__planetRigActivePointerId !== e.pointerId) return;
      __planetRigActivePointerId = null;
      lastInputT.current = performance.now();
    };

    // Wheel listener for zoom.
    const onWheel = (e: WheelEvent) => {
      if (modeRef.current !== 'exploration') return;
      // Don't zoom while the user is scrolling a HUD panel.
      const target = e.target as HTMLElement | null;
      if (target && target.closest('.hud-panel, .hud-root')) return;
      e.preventDefault();
      const factor = Math.exp(e.deltaY * 0.0015);
      distanceRef.current = Math.max(
        EXPLORATION_ZOOM_MIN,
        Math.min(EXPLORATION_ZOOM_MAX, distanceRef.current * factor),
      );
      lastInputT.current = performance.now();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerCancel);
    document.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      window.clearInterval(id);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
      document.removeEventListener('wheel', onWheel);
      if (modeRef.current === 'exploration') {
        window.__swipeDisabled = false;
      }
    };
  }, [camera]);

  // -----------------------------------------------------------------
  // Dismiss info card hook — exposed via window so the HUD card can
  // call back into us to resume auto-orbit cleanly.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__planetDismissInfoCard = () => {
      // Snap the camera back to its pre-fly spherical position so the
      // auto-orbit math resumes from a sensible point.
      const p = camera.position;
      const dist = Math.max(0.001, Math.hypot(p.x, p.y, p.z));
      distanceRef.current = dist;
      azimuthRef.current = Math.atan2(p.x, p.z);
      elevationRef.current = Math.asin(p.y / dist);
      lastInputT.current = performance.now();
      flyT0.current = null;
    };
    return () => {
      window.__planetDismissInfoCard = undefined;
    };
  }, [camera]);

  // -----------------------------------------------------------------
  // Public imperative handle: fly to a world position.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    (window as any).__planetFlyTo = (pos: THREE.Vector3, look: THREE.Vector3) => {
      flyT0.current = performance.now();
      flyFromPos.current.copy(camera.position);
      flyFromLook.current.copy(tmpLook.current);
      // Approach from 1.5 units away along the (target - planet center)
      // direction, looking at the target.
      const dir = pos.clone();
      if (dir.lengthSq() < 0.0001) dir.set(0, 0, 1);
      dir.normalize();
      flyTargetPos.current.copy(pos).addScaledVector(dir, 1.5);
      flyTargetLook.current.copy(pos);
    };
    return () => {
      (window as any).__planetFlyTo = undefined;
    };
  }, [camera]);

  // -----------------------------------------------------------------
  // Per-frame.
  // -----------------------------------------------------------------
  useFrame((state, dt) => {
    const delta = Math.min(dt, 1 / 30);
    const p =
      typeof window !== 'undefined' && typeof window.__planetProgress === 'number'
        ? window.__planetProgress
        : 0;
    const isDragging = isDraggingRef?.current ?? false;
    const reduced =
      typeof window !== 'undefined' && window.__planetReducedMotion === true;

    // Live-read mode so tests / direct window tweaks are honored.
    const mode: PlanetMode =
      (typeof window !== 'undefined' && window.__planetMode) || 'narrative';

    // -------- Tour mode -------------------------------------------
    if (mode === 'tour') {
      // The rig treats the tour as if it were a continuous scroll that
      // useTourController is driving externally. We translate the
      // (sceneIndex, progress) pair into a normalized scroll progress
      // and reuse framedCameraTarget() — same per-scene breathing,
      // same smootherstep on scene boundaries.
      const idx =
        typeof window.__planetTourSceneIndex === 'number'
          ? Math.max(0, Math.min(SCENES.length - 1, window.__planetTourSceneIndex))
          : 0;
      const subProgress =
        typeof window.__planetTourProgress === 'number'
          ? window.__planetTourProgress
          : 0;
      const cur = SCENES[idx];
      const next = SCENES[Math.min(idx + 1, SCENES.length - 1)];
      const span = cur.end - cur.start;
      const p = cur.start + span * Math.max(0, Math.min(1, subProgress));
      framedCameraTarget(p, targetPos.current, targetLook.current);

      // Breathing offset — same as narrative. Skip for reduced motion.
      breathePos.current.copy(targetPos.current);
      if (!reduced) {
        const now = performance.now() / 1000;
        const phase = (now / BREATHE_PERIOD) * TWO_PI;
        breathePos.current.x += Math.sin(phase) * BREATHE_AMPLITUDE;
        breathePos.current.y += Math.cos(phase * 0.5) * BREATHE_AMPLITUDE;
      }

      // If the tour is paused, freeze the camera so the user can read
      // the scene at their leisure. Resume=0 immediately picks the
      // next rAF lerp.
      const tourPaused = !!window.__planetTourPaused;

      // Smooth (lerp) toward the breathing target for a less jittery
      // feel. When transitioning between scenes we bump the lerp
      // factor so the camera tracks the schedule visibly.
      const isTransitioning = subProgress > 0 && subProgress < 1;
      const lerpK = tourPaused
        ? 0
        : isTransitioning
          ? 1 - Math.pow(1 - 0.18, delta * 60)
          : 1 - Math.pow(1 - 0.08, delta * 60);
      camera.position.lerp(breathePos.current, lerpK);
      camera.lookAt(targetLook.current);
      relaxFov(camera, delta);
      return;
    }

    // -------- Exploration mode ------------------------------------
    if (mode === 'exploration') {
      const now = performance.now();

      // Click-to-fly takes precedence over orbit math for its
      // duration.
      if (flyT0.current !== null) {
        const elapsed = now - flyT0.current;
        const t = Math.min(1, elapsed / 800);
        const k = SMOOTHERSTEP(t);
        tmpPos.current.lerpVectors(
          flyFromPos.current,
          flyTargetPos.current,
          k,
        );
        tmpLook.current.lerpVectors(
          flyFromLook.current,
          flyTargetLook.current,
          k,
        );
        camera.position.copy(tmpPos.current);
        camera.lookAt(tmpLook.current);
        if (t >= 1) {
          // Hand control back to the orbit math but keep the camera
          // exactly where it landed.
          flyT0.current = null;
          const p2 = camera.position;
          const d = Math.max(0.001, Math.hypot(p2.x, p2.y, p2.z));
          distanceRef.current = d;
          azimuthRef.current = Math.atan2(p2.x, p2.z);
          elevationRef.current = Math.asin(p2.y / d);
          lastInputT.current = now;
        }
        return;
      }

      // Auto-orbit (unless paused).
      const idleFor = now - lastInputT.current;
      const paused = !!window.__planetAutoOrbitPaused;
      if (!paused && idleFor > EXPLORATION_IDLE_MS && activePointerIdRef() === null) {
        azimuthRef.current += EXPLORATION_AUTO_ORBIT_RAD_PER_S * delta;
      }

      // Compose orbit → world position.
      const cosE = Math.cos(elevationRef.current);
      orbitPos.current.set(
        distanceRef.current * cosE * Math.sin(azimuthRef.current),
        distanceRef.current * Math.sin(elevationRef.current),
        distanceRef.current * cosE * Math.cos(azimuthRef.current),
      );

      // Smooth (lerp) toward the orbit target for a less jittery feel.
      const lerpK = 1 - Math.pow(1 - 0.18, delta * 60);
      camera.position.lerp(orbitPos.current, lerpK);
      camera.lookAt(0, 0, 0);
      relaxFov(camera, delta);

      // Telemetry for tests / external readers.
      if (typeof window !== 'undefined') {
        window.__planetOrbit = {
          azimuth: azimuthRef.current,
          elevation: elevationRef.current,
          distance: distanceRef.current,
        };
      }
      return;
    }

    // -------- Narrative mode (with optional transition hold/ease) -

    // Transition handling: when switching from exploration → narrative,
    // we hold the camera for 800ms, then ease back over 1200ms.
    if (transitionPhase.current !== 'idle') {
      const now = performance.now();
      const elapsed = now - (transitionT0.current ?? now);
      if (transitionPhase.current === 'hold') {
        // Hold position exactly — camera.lookAt is already pointed at
        // planet center from exploration, which is a fine neutral.
        camera.lookAt(0, 0, 0);
        if (elapsed >= TRANSITION_HOLD_MS) {
          transitionPhase.current = 'ease';
          transitionT0.current = now;
        }
        return;
      }
      // Ease phase.
      const t = Math.min(1, elapsed / TRANSITION_EASE_MS);
      const k = SMOOTHERSTEP(t);
      tmpPos.current.lerpVectors(
        transitionFromPos.current,
        transitionTargetPos.current,
        k,
      );
      tmpLook.current.lerpVectors(
        transitionFromLook.current,
        transitionTargetLook.current,
        k,
      );
      camera.position.lerp(tmpPos.current, 0.25);
      camera.lookAt(tmpLook.current);
      if (t >= 1) {
        transitionPhase.current = 'idle';
        transitionT0.current = null;
      }
      return;
    }

    // Standard scroll-driven target.
    framedCameraTarget(p, targetPos.current, targetLook.current);

    if (isDragging) {
      // Freeze at the current scene anchor.
      const scene = getActiveScene(p);
      tmpPos.current.set(
        scene.cameraPos[0],
        scene.cameraPos[1],
        scene.cameraPos[2],
      );
      tmpLook.current.set(
        scene.cameraLookAt[0],
        scene.cameraLookAt[1],
        scene.cameraLookAt[2],
      );
      holdPos.current.copy(tmpPos.current);
      holdLook.current.copy(tmpLook.current);
      camera.position.lerp(tmpPos.current, 0.18);
      camera.lookAt(tmpLook.current);
      releaseT0.current = null;
      lastIsDragging.current = true;
      return;
    }

    // If we just released, kick off a 600ms return lerp from the held
    // anchor toward the current target.
    if (lastIsDragging.current && releaseT0.current === null) {
      releaseT0.current = performance.now();
      releaseFromPos.current.copy(holdPos.current);
      releaseFromLook.current.copy(holdLook.current);
    }
    lastIsDragging.current = false;

    let desiredPos = targetPos.current;
    let desiredLook = targetLook.current;

    if (releaseT0.current !== null) {
      const elapsed = performance.now() - releaseT0.current;
      const t = Math.min(1, elapsed / 600);
      const k = SMOOTHERSTEP(t);
      tmpPos.current.lerpVectors(releaseFromPos.current, targetPos.current, k);
      tmpLook.current.lerpVectors(releaseFromLook.current, targetLook.current, k);
      desiredPos = tmpPos.current;
      desiredLook = tmpLook.current;
      if (t >= 1) {
        releaseT0.current = null;
      }
    }

    // Breathing offset — subtle organic sway. Skipped for reduced motion.
    breathePos.current.copy(desiredPos);
    if (!reduced) {
      const now = performance.now() / 1000;
      const phase = (now / BREATHE_PERIOD) * TWO_PI;
      breathePos.current.x += Math.sin(phase) * BREATHE_AMPLITUDE;
      breathePos.current.y += Math.cos(phase * 0.5) * BREATHE_AMPLITUDE;
    }

    // Per-frame smoothing toward the breathing target.
    camera.position.lerp(breathePos.current, 0.12);
    camera.lookAt(desiredLook);

    // Scroll-velocity flight feel — roll into the scroll direction and
    // stretch the FOV slightly at speed, both spring back to rest.
    // Skipped entirely for reduced motion (velocity is forced to 0
    // upstream, but we also avoid the projection churn here).
    const vel = reduced ? 0 : window.__planetScrollVelocity ?? 0;
    const springK = 1 - Math.pow(1 - 0.1, delta * 60);
    const targetRoll = THREE.MathUtils.clamp(-vel * 0.4, -ROLL_MAX, ROLL_MAX);
    rollRef.current += (targetRoll - rollRef.current) * springK;
    if (Math.abs(rollRef.current) > 0.0004) {
      camera.rotateZ(rollRef.current);
    }
    const pc = camera as THREE.PerspectiveCamera;
    if (pc.isPerspectiveCamera) {
      const targetFov =
        BASE_FOV + Math.min(Math.abs(vel) * 10, FOV_STRETCH_MAX);
      if (Math.abs(pc.fov - targetFov) > 0.02) {
        pc.fov += (targetFov - pc.fov) * springK;
        pc.updateProjectionMatrix();
      }
    }
  });

  return null;
}

// Helper: a stable-ish function the frame loop can call to ask "is a
// pointer drag currently active?". We close over a module-level ref so
// we don't need to re-bind the frame callback when the listener state
// changes (it's already re-created on every render, but the ref pattern
// keeps the read cheap and decoupled).
let __planetRigActivePointerId: number | null = null;
function activePointerIdRef(): number | null {
  return __planetRigActivePointerId;
}
export function __setRigActivePointerId(id: number | null) {
  __planetRigActivePointerId = id;
}

export default SceneCameraRig;
